import { not, or } from 'tio-ui/utilities';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import Divider from 'tio-reimagined/components/divider';
import { concat, fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { Section } from 'tio-reimagined/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import CustomFieldsApproverReview from '../custom-fields-approver-review.gts';
import DefinitionList from '../../ui/definition-list.gts';
import { divide } from 'tio-ui/math';
import EditProgramInfoModal from './edit-program-info-modal.gts';
import formatDateString from '../../../helpers/format-date-string.ts';
import humanize from '../../../helpers/humanize.ts';
import isProgramVisible from '../../../helpers/is-program-visible.ts';
import programLabelFor from '../../../helpers/program-label-for.ts';
import recognizeRoute from '../../../helpers/recognize-route.ts';
import safeFormatNumber from '../../../helpers/safe-format-number.ts';
import type IntlService from 'ember-intl/services/intl';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import { PhPencil, PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';

interface TuitionAssistanceProgramDetailsProgramInfoCardSignature {
  Args: {
    programInstance: TasProgramInstanceModel;
    showAttachments: boolean;
    canEditTasApplication?: boolean;
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceProgramDetailsProgramInfoCardComponent extends Component<TuitionAssistanceProgramDetailsProgramInfoCardSignature> {
  @service declare intl: IntlService;
  @tracked isExpanded = false;
  @tracked showEditProgramInfoModal = false;

  get allowEditProgramDialogToAdmin() {
    return this.args.canEditTasApplication || false;
  }

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ProgramInstanceState.UPDATED_ON');
  };

  <template>
    <Section>
      <:header>
        <div class="flex flex-wrap gap-x-4">
          {{t "tuition_assistance.program_details.program_info.default"}}
          {{#if @programInstance.isDependentProgram}}
            <Chip @size="sm" @intent="default">Dependent Program</Chip>
          {{/if}}
        </div>
      </:header>
      <:subHeader>{{t "tuition_assistance.id"}}{{@programInstance.id}}</:subHeader>
      <:actions>
        {{#if this.allowEditProgramDialogToAdmin}}
          <Button
            @intent="primary"
            {{on "click" (fn (mut this.showEditProgramInfoModal) true)}}
            title={{t "common.edit"}}
          >
            <PhPencil @size="16" />
          </Button>
        {{/if}}
      </:actions>
      <:body>
        <DefinitionList as |options|>
          {{#if @programInstance.isDependentProgram}}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "DEPENDENT_NAME"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>{{@programInstance.dependentName}}</options.dd>
            </div>
          {{/if}}
          <div class={{options.wrappingClass}}>
            <options.dt>
              {{programLabelFor "PROGRAM_NAME" programTemplate=@programInstance.tasProgramTemplate}}
            </options.dt>
            <options.dd>{{@programInstance.programName}}</options.dd>
          </div>
          {{#if
            (isProgramVisible
              "PROGRAM_SPECIALIZATION" programTemplate=@programInstance.tasProgramTemplate
            )
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "PROGRAM_SPECIALIZATION"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{@programInstance.programSpecialization}}
              </options.dd>
            </div>
          {{/if}}
          {{#if
            (isProgramVisible "PROGRAM_MAJOR" programTemplate=@programInstance.tasProgramTemplate)
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "PROGRAM_MAJOR"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{@programInstance.programMajor}}
              </options.dd>
            </div>
          {{/if}}

          {{#if
            (isProgramVisible
              "SCHOOL_INSTITUTION_NAME" programTemplate=@programInstance.tasProgramTemplate
            )
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "SCHOOL_INSTITUTION_NAME"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{@programInstance.institutionName}}
              </options.dd>
            </div>
          {{/if}}

          {{#if
            (isProgramVisible
              "ESTIMATED_PROGRAM_BEGIN" programTemplate=@programInstance.tasProgramTemplate
            )
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "ESTIMATED_PROGRAM_BEGIN"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{formatDateString @programInstance.estimatedProgramBegin}}
              </options.dd>
            </div>
          {{/if}}

          {{#if
            (isProgramVisible
              "ESTIMATED_PROGRAM_COMPLETION" programTemplate=@programInstance.tasProgramTemplate
            )
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "ESTIMATED_PROGRAM_COMPLETION"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{formatDateString @programInstance.estimatedProgramCompletion}}
              </options.dd>
            </div>
          {{/if}}

          {{#if
            (isProgramVisible "ESTIMATED_COST" programTemplate=@programInstance.tasProgramTemplate)
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "ESTIMATED_COST"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{safeFormatNumber
                  (divide @programInstance.estimatedCost 100)
                  style="currency"
                  currency="USD"
                }}
              </options.dd>
            </div>
          {{/if}}

          {{#if
            (isProgramVisible
              "ENROLLMENT_OBJECTIVE" programTemplate=@programInstance.tasProgramTemplate
            )
          }}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor
                  "ENROLLMENT_OBJECTIVE"
                  programTemplate=@programInstance.tasProgramTemplate
                }}
              </options.dt>
              <options.dd>
                {{humanize @programInstance.enrollmentObjective}}
              </options.dd>
            </div>
          {{/if}}

          {{#if @canEditTasApplication}}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{programLabelFor "TYPE" programTemplate=@programInstance.tasProgramTemplate}}
              </options.dt>
              <options.dd>
                {{t (or (concat "common.tas.types." @programInstance.typeClassification) "Legacy")}}
              </options.dd>
            </div>
          {{/if}}

          {{#unless @programInstance.tasProgramTemplate.isCertificateProgram}}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{t "common.status"}}
              </options.dt>
              <options.dd>
                {{this.statusText @programInstance.state}}
              </options.dd>
            </div>
          {{/unless}}
        </DefinitionList>
        <Divider class="my-2" />

        <div class="flex text-sm md:text-base">
          {{#if (recognizeRoute "authenticated.tuition-assistance.program-overview")}}
            <LinkTo
              @route="authenticated.tuition-assistance.program-overview"
              @model={{@programInstance.tasProgramTemplate.id}}
              class="underline text-ocean-600"
            >
              {{t "tuition_assistance.program_details.program_info.click_here_pt_1"}}
            </LinkTo>
            <p class="ml-1">
              {{t "tuition_assistance.program_details.program_info.click_here_pt_2"}}
            </p>
          {{/if}}
        </div>

        <div class="flex justify-start md:text-base text-sm text-tio-gray-600">
          {{#if (recognizeRoute "authenticated.admin.tuition-assistance.applications.show.index")}}
            <LinkTo
              @route="authenticated.admin.tuition-assistance.program-templates.show"
              @model={{@programInstance.tasProgramTemplate.id}}
              class="underline text-ocean-600"
            >
              {{t "tuition_assistance.program_details.program_info.click_here_pt_1"}}
            </LinkTo>
            <p class="ml-1">
              {{t "tuition_assistance.program_details.program_info.click_here_pt_2"}}
            </p>
          {{/if}}
          {{#if (recognizeRoute "authenticated.tuition-assistance.applications.show.index")}}
            <LinkTo
              @route="authenticated.tuition-assistance.program-builder.program-template"
              @model={{@programInstance.tasProgramTemplate.id}}
              class="underline text-ocean-600"
            >
              {{t "tuition_assistance.program_details.program_info.click_here_pt_1"}}
            </LinkTo>
            <p class="ml-1">
              {{t "tuition_assistance.program_details.program_info.click_here_pt_2"}}
            </p>
          {{/if}}
        </div>

        {{#if this.isExpanded}}
          <CustomFieldsApproverReview
            @customFields={{@programInstance.customFields}}
            @assets={{@programInstance.tasAssets}}
            @province="PROGRAM"
          />
        {{/if}}

        {{#if @showAttachments}}
          <div class="flex justify-center mt-3">
            <button
              type="button"
              class="font-semibold text-sm text-tio-gray-600"
              {{on "click" (fn (mut this.isExpanded) (not this.isExpanded))}}
            >
              <span class="flex items-center">
                {{#if this.isExpanded}}
                  <PhCaretDown @size="16" class="mr-1" />
                {{else}}
                  <PhCaretUp @size="16" class="mr-1" />
                {{/if}}
                {{t "tuition_assistance.program_details.program_info.view_details_and_attachments"}}
              </span>
            </button>
          </div>
        {{/if}}
      </:body>
    </Section>

    <EditProgramInfoModal
      @programInstance={{@programInstance}}
      @onClose={{fn (mut this.showEditProgramInfoModal) false}}
      @isOpen={{this.showEditProgramInfoModal}}
    />
  </template>
}
